const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const formatMessage = require('format-message');
const Video = require('../../io/video');
const tf = require('@tensorflow/tfjs');
const handpose = require('@tensorflow-models/handpose');
const StageLayering = require('../../engine/stage-layering');
class Scratch3HandposeDetectionExtension {
    constructor(runtime) {
        this.runtime = runtime;
        this.video = this.runtime.ioDevices.video;
        this.model = null;
        this.landmarks = [];
        this.loadingModel = false;
        this.detectionInProgress = false;
        this.activeDetectionPromise = null;
        this.isDetectionVisible = true;
        this.handX = 0;
        this.handY = 0;
        this.handWidth = 0;
        this.handHeight = 0;
        this.penSkinId = -1;
        this.penDrawableId = -1;
        this._initPromise = this._initialize();
        this._initializePenLayer();

        // Turn off camera and clear drawings when a project is loaded or switched
        this._onProjectLoaded = this._cleanup.bind(this);
        if (this.runtime) {
            this.runtime.on('PROJECT_LOADED', this._onProjectLoaded);
            this.runtime.on('RUNTIME_DISPOSED', this._onProjectLoaded);
        }
    }

    // Create a loading popup UI manager
    _createLoadingUI() {
        // Create overlay first
        const overlay = document.createElement('div');
        overlay.className = 'stemblocks-detection-overlay';
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.5);
            backdrop-filter: blur(3px);
            z-index: 999;
        `;
        document.body.appendChild(overlay);

        // Create style element for animations
        const styleSheet = document.createElement('style');
        styleSheet.textContent = `
        @keyframes spin {
            to { transform: rotate(360deg); }
        }
        @keyframes bounce {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-10px); }
        }
        @keyframes scale {
            0% { transform: scale(0.5); }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); }
        }
        @font-face {
            font-family: 'Comic Sans MS';
            src: local('Comic Sans MS');
        }
    `;
        document.head.appendChild(styleSheet);

        // Create popup container
        const popup = document.createElement('div');
        popup.className = 'stemblocks-detection-popup';
        popup.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: linear-gradient(135deg, #6e8efb, #a777e3);
        color: white;
        padding: 25px 30px;
        border-radius: 20px;
        z-index: 1000;
        font-family: 'Comic Sans MS', 'Marker Felt', 'Arial Rounded MT Bold', sans-serif;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 15px;
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
        min-width: 280px;
        text-align: center;
        border: 4px solid white;
    `;

        // Create spinner with fun design
        const spinner = document.createElement('div');
        spinner.style.cssText = `
        width: 60px;
        height: 60px;
        border: 5px solid rgba(255, 255, 255, 0.5);
        border-radius: 50%;
        border-top-color: white;
        animation: spin 1s ease-in-out infinite;
        box-shadow: 0 0 15px rgba(255, 255, 255, 0.5);
    `;

        // Create title with emoji
        const title = document.createElement('div');
        title.style.cssText = `
        font-size: 20px;
        font-weight: bold;
        letter-spacing: 0.5px;
        color: #fff;
        text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
    `;
        title.innerHTML = '🤖 Loading AI Model...';

        // Create message text
        const message = document.createElement('div');
        message.style.cssText = `
        font-size: 16px;
        opacity: 0.9;
        margin-top: -5px;
    `;
        message.textContent = 'Please wait while model is being loaded!';

        // Create fun loading animation
        const loadingDots = document.createElement('div');
        loadingDots.style.cssText = `
        display: flex;
        gap: 8px;
        margin-top: 5px;
    `;

        // Add bouncing dots
        for (let i = 0; i < 3; i++) {
            const dot = document.createElement('div');
            dot.style.cssText = `
            width: 12px;
            height: 12px;
            background-color: white;
            border-radius: 50%;
            animation: bounce 0.8s ease-in-out infinite;
            animation-delay: ${i * 0.15}s;
        `;
            loadingDots.appendChild(dot);
        }

        // Assemble popup
        popup.appendChild(title);
        popup.appendChild(spinner);
        popup.appendChild(message);
        popup.appendChild(loadingDots);
        document.body.appendChild(popup);

        return { popup, spinner, title, message, loadingDots, overlay };
    }

    // Update UI for success state
    _showSuccessUI(elements) {
        const { popup, spinner, title, message, loadingDots, overlay } = elements;

        // Update spinner to success icon
        spinner.style.borderWidth = '0';
        spinner.innerHTML = '✨';
        spinner.style.cssText = `
        width: 70px;
        height: 70px;
        background: #4ede4e;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 40px;
        color: white;
        animation: scale 0.5s ease-in-out;
        box-shadow: 0 0 20px rgba(78, 222, 78, 0.5);
    `;

        // Update text
        title.innerHTML = '🎉 All Ready!';
        message.textContent = 'Model loaded successfully!';

        // Remove loading dots
        if (loadingDots.parentNode === popup) {
            popup.removeChild(loadingDots);
        }

        // Fade out and remove
        setTimeout(() => {
            if (document.body.contains(popup)) {
                popup.style.opacity = '0';
                overlay.style.opacity = '0';
                popup.style.transform = 'translate(-50%, -50%) scale(0.8)';
                popup.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
                overlay.style.transition = 'opacity 0.6s ease';
                setTimeout(() => {
                    if (document.body.contains(popup)) {
                        document.body.removeChild(popup);
                        document.body.removeChild(overlay);
                    }
                }, 600);
            }
        }, 1500);
    }

    // Update UI for error state
    _showErrorUI(elements) {
        const { popup, spinner, title, message, loadingDots, overlay } = elements;

        // Update spinner to error icon
        spinner.style.cssText = `
        width: 70px;
        height: 70px;
        background: #ff6b6b;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 40px;
        color: white;
        box-shadow: 0 0 20px rgba(255, 107, 107, 0.5);
    `;
        spinner.innerHTML = '😕';

        // Update text
        title.innerHTML = 'Oops! Something went wrong';
        message.textContent = 'Try refreshing the page or check your internet!';

        // Remove loading dots
        if (loadingDots.parentNode === popup) {
            popup.removeChild(loadingDots);
        }

        // Add retry button
        const retryButton = document.createElement('button');
        retryButton.textContent = 'Try Again';
        retryButton.style.cssText = `
        background: white;
        color: #ff6b6b;
        border: none;
        border-radius: 30px;
        padding: 10px 20px;
        font-family: inherit;
        font-size: 16px;
        font-weight: bold;
        cursor: pointer;
        margin-top: 10px;
        transition: all 0.2s;
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    `;
        retryButton.onmouseover = () => {
            retryButton.style.transform = 'scale(1.05)';
        };
        retryButton.onmouseout = () => {
            retryButton.style.transform = 'scale(1)';
        };
        retryButton.onclick = () => {
            if (document.body.contains(popup)) {
                document.body.removeChild(popup);
                document.body.removeChild(overlay);
            }
            this._initialize(); // Restart initialization
        };
        popup.appendChild(retryButton);

        // Fade out after longer time
        setTimeout(() => {
            if (document.body.contains(popup)) {
                popup.style.opacity = '0';
                overlay.style.opacity = '0';
                popup.style.transform = 'translate(-50%, -50%) scale(0.8)';
                popup.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
                overlay.style.transition = 'opacity 0.6s ease';
                setTimeout(() => {
                    if (document.body.contains(popup)) {
                        document.body.removeChild(popup);
                        document.body.removeChild(overlay);
                    }
                }, 600);
            }
        }, 5000);
    }

    async _initialize() {
        await tf.ready();

        // Create and show loading UI
        const uiElements = this._createLoadingUI();

        try {
            // Load the handpose model
            this.model = await handpose.load();
            this.modelLoaded = true;
            console.log('Handpose model loaded successfully');

            // Show success UI
            this._showSuccessUI(uiElements);
        } catch (error) {
            console.error('Failed to load handpose model:', error);

            // Show error UI
            this._showErrorUI(uiElements);
        }
    }


    getInfo() {
        return {
            id: 'HumanBody',
            color1: '#FF5733',
            name: formatMessage({
                id: 'HumanBody.categoryName',
                default: 'Hand Pose Detection',
                description: 'Label for the hand pose detection extension category'
            }),
            blocks: [
                {
                    opcode: 'videoToggle',
                    text: formatMessage({
                        id: 'HumanBody.videoToggle',
                        default: 'turn video [VIDEO_STATE]',
                        description: 'Controls display of the video preview layer'
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {
                        VIDEO_STATE: {
                            type: ArgumentType.STRING,
                            menu: 'VIDEO_STATE',
                            defaultValue: 'off'
                        }
                    }
                },
                {
                    opcode: 'detectHandPose',
                    text: formatMessage({
                        id: 'HumanBody.detectHandPose',
                        default: 'detect hand pose',
                        description: 'Detects hand pose and shows landmarks on the screen'
                    }),
                    blockType: BlockType.COMMAND
                },


                {
                    opcode: 'moveSpriteWithHand',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'HumanBody.moveSpriteWithHand',
                        default: 'go to [FINGER] [PART]',
                        description: 'Move sprite according to hand movement'
                    }),
                    arguments: {
                        FINGER: {
                            type: ArgumentType.STRING,
                            menu: 'fingers',
                            defaultValue: 'thumb'
                        },
                        PART: {
                            type: ArgumentType.STRING,
                            menu: 'parts',
                            defaultValue: 'tip'
                        }
                    }
                },


                {
                    opcode: 'isHandDetected',
                    blockType: BlockType.BOOLEAN,
                    text: formatMessage({
                        id: 'HumanBody.isHandDetected',
                        default: 'is hand detected',
                        description: 'Boolean reporter for hand detection'
                    })
                },
                {
                    opcode: 'setDetectionVisibility',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'HumanBody.setDetectionVisibility',
                        default: '[VISIBILITY_STATE] detections',
                        description: 'Command to show or hide hand detections'
                    }),
                    arguments: {
                        VISIBILITY_STATE: {
                            type: ArgumentType.STRING,
                            menu: 'visibilityStates',
                            defaultValue: 'show'
                        }
                    }
                },
                {
                    opcode: 'getFingerPosition',
                    blockType: BlockType.REPORTER,
                    text: formatMessage({
                        id: 'HumanBody.getFingerPosition',
                        default: 'get[POSITION] [FINGER] finger position',
                        description: 'Gets the position of a specific finger'
                    }),
                    arguments: {
                        FINGER: {
                            type: ArgumentType.STRING,
                            menu: 'fingers',
                            defaultValue: 'index'
                        },
                        POSITION: {
                            type: ArgumentType.STRING,
                            menu: 'coordinates',
                            defaultValue: 'x' // Default to 'x' coordinate
                        }
                    }
                },
                {
                    opcode: 'getHandPosition',
                    blockType: BlockType.REPORTER,
                    text: formatMessage({
                        id: 'HumanBody.getHandPosition',
                        default: 'get[POSITION] hand position',
                        description: 'Gets the X or Y position of the hand'
                    }),
                    arguments: {
                        POSITION: {
                            type: ArgumentType.STRING,
                            menu: 'coordinates',
                            defaultValue: 'x' // Default to 'x' coordinate
                        }
                    }
                },


                {
                    opcode: 'clearCanvas',
                    text: formatMessage({
                        id: 'HumanBody.clearCanvas',
                        default: 'clear canvas',
                        description: 'Clears the drawing on the canvas'
                    }),
                    blockType: BlockType.COMMAND,
                    arguments: {}
                }


            ],
            menus: {
                VIDEO_STATE: {
                    acceptReporters: true,
                    items: [
                        { text: 'on', value: 'on' },
                        { text: 'off', value: 'off' }
                    ]
                },
                handProperties: {
                    acceptReporters: false,
                    items: ['x position', 'y position', 'width', 'height']
                },
                coordinates: {
                    acceptReporters: false,
                    items: ['x', 'y']
                },
                // 
                fingers: {
                    acceptReporters: false,
                    items: ['thumb', 'index', 'middle', 'ring', 'pinky']
                },
                parts: {
                    acceptReporters: false,
                    items: ['tip', 'middle', 'base']
                },
                visibilityStates: {
                    acceptReporters: false,
                    items: ['show', 'hide']
                },


            }

        };
    }

    videoToggle(args) {
        const state = args.VIDEO_STATE;
        if (state === 'off') {
            this.runtime.ioDevices.video.disableVideo();
            try {
                localStorage.setItem('isCameraOpen', JSON.stringify(false));
            } catch (e) {}
        } else {
            this.runtime.ioDevices.video.enableVideo().then(() => {
                this.runtime.ioDevices.video.mirror = true;
                try {
                    localStorage.setItem('isCameraOpen', JSON.stringify(true));
                } catch (e) {}
            }).catch(err => {
                console.error("Error enabling video: ", err);
            });
        }
    }

    _initializePenLayer() {
        if (this.penSkinId < 0 && this.runtime.renderer) {
            this.penSkinId = this.runtime.renderer.createPenSkin();
            this.penDrawableId = this.runtime.renderer.createDrawable(StageLayering.PEN_LAYER);
            this.runtime.renderer.updateDrawableSkinId(this.penDrawableId, this.penSkinId);
            console.log('Pen layer initialized:', {
                penSkinId: this.penSkinId,
                penDrawableId: this.penDrawableId
            });
        }
    }

    async _getVideoElement(maxWaitMs = 500) {
        if (!this.video) return null;
        const startTime = Date.now();
        while (Date.now() - startTime <= maxWaitMs) {
            const video = this.video.provider && this.video.provider.video;
            if (video && video.videoWidth > 0 && video.videoHeight > 0 && video.readyState >= 2) {
                return video;
            }
            await new Promise(resolve => setTimeout(resolve, 50));
        }
        return null;
    }

    async detectTheHandPose() {
        // 1. Wait for model to finish loading if it hasn't yet
        if (!this.model && this._initPromise) {
            await this._initPromise;
        }

        if (!this.model) {
            return false;
        }

        // 2. If a detection is already in progress, reuse its promise instead of failing
        if (this.activeDetectionPromise) {
            return await this.activeDetectionPromise;
        }

        this.activeDetectionPromise = (async () => {
            let detected = false;
            try {
                const videoElement = await this._getVideoElement(250);
                if (!videoElement) {
                    return false;
                }

                const predictions = await this.model.estimateHands(videoElement);

                if (predictions && predictions.length > 0) {
                    this.landmarks = predictions[0].landmarks;

                    const xs = this.landmarks.map(l => l[0]);
                    const ys = this.landmarks.map(l => l[1]);
                    const minX = Math.min(...xs);
                    const maxX = Math.max(...xs);
                    const minY = Math.min(...ys);
                    const maxY = Math.max(...ys);

                    this.handX = (minX + maxX) / 2;
                    this.handY = (minY + maxY) / 2;
                    this.handWidth = maxX - minX;
                    this.handHeight = maxY - minY;

                    if (this.isDetectionVisible) {
                        detected = true;
                    }
                } else {
                    this.handX = 0;
                    this.handY = 0;
                    this.handWidth = 0;
                    this.handHeight = 0;
                    this._clearPenLayer();
                }
            } catch (err) {
                console.warn('Hand detection frame skipped:', err.message || err);
            } finally {
                this.activeDetectionPromise = null;
            }
            return detected;
        })();

        return await this.activeDetectionPromise;
    }

    async detectHandPose() {
        const visible = await this.detectTheHandPose();
        if (visible) {
            this._drawLandmarks();
        }
    }

    _drawLandmarks() {
        this._clearPenLayer();
        if (this.penSkinId < 0) {
            console.warn('Pen layer is not initialized properly.');
            return;
        }
        // Get the video element
        const videoElement = this.video.provider && this.video.provider.video;

        if (!videoElement || !videoElement.videoWidth || !videoElement.videoHeight) {
            return;
        }

        // Fixed dimensions for Scratch stage
        const stageWidth = 480;
        const stageHeight = 360;

        const penAttributes = {
            color4f: [0, 1, 0, 1], // Lime green color [R, G, B, A]
            diameter: 2
        };

        const dotAttributes = {
            color4f: [1, 0, 0, 1], // Red color for keypoints [R, G, B, A]
            diameter: 4
        };

        // Draw connections between landmarks
        const connections = [
            // Thumb
            [0, 1], [1, 2], [2, 3], [3, 4],
            // Index finger
            [0, 5], [5, 6], [6, 7], [7, 8],
            // Middle finger
            [0, 9], [9, 10], [10, 11], [11, 12],
            // Ring finger
            [0, 13], [13, 14], [14, 15], [15, 16],
            // Pinky
            [0, 17], [17, 18], [18, 19], [19, 20],
            // Palm base
            [5, 9], [9, 13], [13, 17]
        ];

        // Draw skeleton lines
        connections.forEach(([i, j]) => {
            if (this.landmarks[i] && this.landmarks[j]) {
                const [x1, y1] = this.landmarks[i];
                const [x2, y2] = this.landmarks[j];

                // Scale and flip X coordinate for mirroring
                const scratchX1 = (1 - x1 / videoElement.videoWidth) * stageWidth;
                const scratchY1 = (y1 / videoElement.videoHeight) * stageHeight;
                const scratchX2 = (1 - x2 / videoElement.videoWidth) * stageWidth;
                const scratchY2 = (y2 / videoElement.videoHeight) * stageHeight;

                // Convert to Scratch coordinate system (center is 0,0)
                const stageX1 = scratchX1 - (stageWidth / 2);
                const stageY1 = (stageHeight / 2) - scratchY1;
                const stageX2 = scratchX2 - (stageWidth / 2);
                const stageY2 = (stageHeight / 2) - scratchY2;

                this.runtime.renderer.penLine(
                    this.penSkinId,
                    penAttributes,
                    stageX1,
                    stageY1,
                    stageX2,
                    stageY2
                );
            }
        });

        // Draw keypoint dots
        this.landmarks.forEach(([x, y]) => {
            // Scale and flip X coordinate for mirroring
            const scratchX = (1 - x / videoElement.videoWidth) * stageWidth;
            const scratchY = (y / videoElement.videoHeight) * stageHeight;

            // Convert to Scratch coordinate system
            const stageX = scratchX - (stageWidth / 2);
            const stageY = (stageHeight / 2) - scratchY;

            // Draw circle using small line segments
            const segments = 8;
            const radius = 2;
            for (let i = 0; i < segments; i++) {
                const angle1 = (i / segments) * 2 * Math.PI;
                const angle2 = ((i + 1) / segments) * 2 * Math.PI;

                const x1 = stageX + Math.cos(angle1) * radius;
                const y1 = stageY + Math.sin(angle1) * radius;
                const x2 = stageX + Math.cos(angle2) * radius;
                const y2 = stageY + Math.sin(angle2) * radius;

                this.runtime.renderer.penLine(
                    this.penSkinId,
                    dotAttributes,
                    x1,
                    y1,
                    x2,
                    y2
                );
            }
        });

        this.runtime.requestRedraw();
    }

    async moveSpriteWithHand(args) {
        const finger = args.FINGER;
        const part = args.PART;
        await this.detectHandPose();

        if (!this.landmarks || this.landmarks.length === 0) {
            return;
        } else {
            console.log('Hand landmarks:', this.landmarks);
        }

        // Determine which landmark index to use based on finger and part
        let landmarkIndex;
        switch (finger) {
            case 'thumb':
                landmarkIndex = part === 'tip' ? 4 : part === 'middle' ? 3 : part === 'base' ? 2 : 1;
                break;
            case 'index':
                landmarkIndex = part === 'tip' ? 8 : part === 'middle' ? 7 : part === 'base' ? 6 : 5;
                break;
            case 'middle':
                landmarkIndex = part === 'tip' ? 12 : part === 'middle' ? 11 : part === 'base' ? 10 : 9;
                break;
            case 'ring':
                landmarkIndex = part === 'tip' ? 16 : part === 'middle' ? 15 : part === 'base' ? 14 : 13;
                break;
            case 'pinky':
                landmarkIndex = part === 'tip' ? 20 : part === 'middle' ? 19 : part === 'base' ? 18 : 17;
                break;
            default:
                console.error('Invalid finger or part.');
                return;
        }

        const [x, y] = this.landmarks[landmarkIndex];

        // console.log(`Selected finger: ${finger}, part: ${part}, landmark index: ${landmarkIndex}`);
        // console.log(`Hand coordinates (video): x = ${x}, y = ${y}`);

        const video = this.video.provider && this.video.provider.video;
        if (!video || !video.videoWidth || !video.videoHeight) {
            return;
        }
        const videoWidth = video.videoWidth;
        const videoHeight = video.videoHeight;
        const stageWidth = this.runtime.constructor.STAGE_WIDTH;
        const stageHeight = this.runtime.constructor.STAGE_HEIGHT;
        // console.log(videoWidth, videoHeight, stageWidth, stageHeight)

        const spriteX = -1 * ((x / videoWidth - 0.5) * stageWidth);
        const spriteY = (0.5 - y / videoHeight) * stageHeight;

        console.log(`Hand coordinates (Scratch): spriteX = ${spriteX}, spriteY = ${spriteY}`);

        // Move the sprite to the new position
        const sprite = this.runtime.getEditingTarget();
        console.log('Sprite being moved:', sprite.getName());
        sprite.setXY(spriteX, spriteY);


    }
    _clearPenLayer() {
        if (this.penSkinId >= 0) {
            this.runtime.renderer.penClear(this.penSkinId);
            this.runtime.requestRedraw();
        }
    }
    async isHandDetected() {
        return await this.detectTheHandPose();
    }
    setDetectionVisibility(args) {
        const visibilityState = args.VISIBILITY_STATE;
        this.isDetectionVisible = (visibilityState === 'show');

        if (!this.isDetectionVisible) {
            this._clearPenLayer();
        } else if (this.landmarks && this.landmarks.length > 0) {
            this._drawLandmarks();
        }
    }

    getFingerPosition(args) {
        const video = this.video.provider && this.video.provider.video;
        if (!this.landmarks || this.landmarks.length === 0 || !video || !video.videoWidth || !video.videoHeight) {
            return '0';
        }

        const fingerIndices = {
            'thumb': 4,
            'index': 8,
            'middle': 12,
            'ring': 16,
            'pinky': 20
        };

        const fingerIndex = fingerIndices[args.FINGER];
        if (fingerIndex && this.landmarks[fingerIndex]) {
            // Convert coordinates using the same logic as moveSpriteWithHand
            const [x, y] = this.landmarks[fingerIndex];
            const videoWidth = this.video.provider.video.videoWidth;
            const videoHeight = this.video.provider.video.videoHeight;
            const stageWidth = this.runtime.constructor.STAGE_WIDTH;
            const stageHeight = this.runtime.constructor.STAGE_HEIGHT;

            const scratchX = -1 * ((x / videoWidth - 0.5) * stageWidth);
            const scratchY = (0.5 - y / videoHeight) * stageHeight;

            // If POSITION is not specified, default to 'x'
            if (args.POSITION === 'y') {
                return Math.round(scratchY).toString(); // Return only Y coordinate
            } else {
                // Default to 'x' if POSITION is not provided or is 'x'
                return Math.round(scratchX).toString(); // Return only X coordinate
            }
        }
        return '0';
    }



    getHandPosition(args) {
        const video = this.video.provider && this.video.provider.video;
        if (!this.landmarks || this.landmarks.length === 0 || !video || !video.videoWidth || !video.videoHeight) {
            return '0';
        }

        // Hand keypoints typically refer to the center or the palm
        // In this case, we can use an index like the middle of the palm (landmark 0 or the palm landmark)
        const handIndex = 0; // Palm center or similar central landmark

        if (this.landmarks[handIndex]) {
            // Get the X and Y coordinates for the hand (palm)
            const [x, y] = this.landmarks[handIndex];
            const videoWidth = this.video.provider.video.videoWidth;
            const videoHeight = this.video.provider.video.videoHeight;
            const stageWidth = this.runtime.constructor.STAGE_WIDTH;
            const stageHeight = this.runtime.constructor.STAGE_HEIGHT;

            const scratchX = -1 * ((x / videoWidth - 0.5) * stageWidth);
            const scratchY = (0.5 - y / videoHeight) * stageHeight;

            // Return either X or Y coordinate based on user selection
            if (args.POSITION === 'y') {
                return Math.round(scratchY).toString(); // Return Y position of hand
            } else {
                return Math.round(scratchX).toString(); // Return X position of hand
            }
        }
        return '0'; // Return 0 if no landmarks available
    }

    clearCanvas() {
        this._clearPenLayer();
        if (typeof document !== 'undefined') {
            try {
                const overlays = document.querySelectorAll('.stemblocks-detection-overlay, .stemblocks-detection-popup');
                overlays.forEach(el => el.remove());
            } catch (e) {}
        }
    }

    /**
     * Turn off camera, clear pen layer, and remove overlays.
     * Called automatically on PROJECT_LOADED / RUNTIME_DISPOSED.
     */
    _cleanup() {
        // Turn off the camera
        try {
            if (this.video) {
                this.video.disableVideo();
            }
        } catch (e) {}

        // Clear pen drawings
        this._clearPenLayer();

        // Remove any loading/error popups still in the DOM
        if (typeof document !== 'undefined') {
            try {
                const overlays = document.querySelectorAll('.stemblocks-detection-overlay, .stemblocks-detection-popup');
                overlays.forEach(el => el.remove());
            } catch (e) {}
        }

        // Reset detection state
        this.landmarks = [];
        this.handX = 0;
        this.handY = 0;
        this.handWidth = 0;
        this.handHeight = 0;
        this.activeDetectionPromise = null;
    }

    dispose() {
        // Deregister event listeners to avoid memory leaks
        if (this.runtime && this._onProjectLoaded) {
            this.runtime.removeListener('PROJECT_LOADED', this._onProjectLoaded);
            this.runtime.removeListener('RUNTIME_DISPOSED', this._onProjectLoaded);
        }
        this._cleanup();
    }

}

module.exports = Scratch3HandposeDetectionExtension;